<script setup lang="ts">
import useMedia from '@/composables/media'
import { useContestPreviewFrame } from '../composables/contestPreviewFrame'

import type { ContestGameReward, TemplateDetailContests } from '@/types/types'

const props = withDefaults(
  defineProps<{
    detailContests: TemplateDetailContests
    hideBottom?: boolean
    scale?: number
  }>(),
  {
    hideBottom: true,
    scale: 1
  }
)

const { t, te } = useI18n()
const { merchantLogo, merchantName, user } = useLocalStorage()
const { getContrast } = useColor()
const { isDefaultLogo } = useMedia()
const { getMerchantColors } = useMerchantColors()

const defaultBackgroundColor = '#ddd6fe'

const isLoaded = ref(false)
const colors = ref({
  backgroundColor: defaultBackgroundColor,
  buttonColor: '#FFFFFF',
  buttonTextColor: defaultBackgroundColor,
  textColor: defaultBackgroundColor,
  losingTileColor: '#FF0000',
  losingTileTextColor: '#FFFFFF'
})
const contestGameRewards = ref<ContestGameReward[]>([])

const logo = computed(() => {
  const merchantMedia = merchantLogo.value || `${import.meta.env.VITE_AWS_STORAGE_URL}/logo.png`

  return isDefaultLogo(merchantMedia) ? null : merchantMedia
})

const setDefaultColors = (primaryColor: string) => {
  const primaryColorContrast = getContrast(primaryColor)

  colors.value.backgroundColor = primaryColor
  colors.value.buttonTextColor = primaryColor
  colors.value.textColor = primaryColorContrast
  colors.value.buttonColor = primaryColorContrast

  contestGameRewards.value = props.detailContests.contestGameRewards.map((contestGameReward, contestIndex) =>
    contestIndex % 2 === 0
      ? { ...contestGameReward, color: primaryColorContrast, textColor: primaryColor }
      : { ...contestGameReward, color: primaryColor, textColor: primaryColorContrast }
  )
}

const optinText = computed(() =>
  te(props.detailContests.optinText) ? t(props.detailContests.optinText) : props.detailContests.optinText
)

const buildPayload = () => ({
  contest: {
    ...props.detailContests,
    optinText: optinText.value,
    ...colors.value,
    contestGameRewards: contestGameRewards.value,
    logoUrl: logo.value,
    coverImageUrl: null,
    isOtpRequired: false,
    isPlayableManyTimes: true
  },
  mechanic: props.detailContests.mechanic || 'fortuneWheel',
  boostSlug: 'none',
  merchant: {
    id: user.value.merchant.id,
    name: merchantName.value,
    logo: logo.value || '',
    webAppUrl: user.value.merchant.webAppUrl
  },
  storeName: merchantName.value
})

const { iframeRef, previewUrl } = useContestPreviewFrame(buildPayload)

onMounted(async () => {
  const merchantColors = await getMerchantColors()

  setDefaultColors(merchantColors?.background || defaultBackgroundColor)
  isLoaded.value = true
})
</script>

<template>
  <BasePhone v-if="previewUrl && isLoaded" size="md" :hide-bottom="hideBottom" :scale="scale">
    <iframe
      ref="iframeRef"
      :src="previewUrl"
      class="w-full h-full"
      title="Contest preview"
      sandbox="allow-same-origin allow-scripts allow-modals allow-popups"
    />
  </BasePhone>

  <AppSkeleton v-else-if="previewUrl" type="text" class="h-full w-full" />
</template>
